import { Button } from '@src/components/ui/button';

import useCurrentLang from '@shared/hooks/useCurrentLang';
import type {
    ServiceFieldTableRowDto,
    ServiceProcessFieldTableColumnVM,
} from '@shared/types/process';

import { formatTableCellDisplayValue, getColumnTitle } from './tableFieldUtils';

import { ShadcnButton } from '@shadcn-features/core';
import { Pencil, Trash2, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
    row: ServiceFieldTableRowDto;
    columns: ServiceProcessFieldTableColumnVM[];
    rowNumber: number;
    disabled?: boolean;
    editDisabled?: boolean;
    readOnly?: boolean;
    onEdit?: () => void;
    onDelete: () => void;
    onCancel?: () => void;
    onSave?: () => void;
    children?: ReactNode;
};

export function TableFieldMobileRow({
    row,
    columns,
    rowNumber,
    disabled,
    editDisabled,
    readOnly,
    onEdit,
    onDelete,
    onCancel,
    onSave,
    children,
}: Props) {
    const { t } = useTranslation();
    const lang = useCurrentLang();
    const editing = Boolean(onSave);
    const recordTitle = `${t('translation:serviceProcessActionTypeRecord')} ${rowNumber}`;
    const actionProps = {
        type: 'button' as const,
        variant: 'outline' as const,
        height: 34,
        paddingX: 0,
        paddingY: 0,
        className:
            'size-[34px] shrink-0 rounded-lg border border-(--color-border) bg-(--color-body) text-(--color-card-subtitle) [&_svg]:size-3.5',
    };

    return (
        <section
            onKeyDown={(event) => {
                if (event.key === 'Escape' && !event.defaultPrevented)
                    onCancel?.();
            }}
            className='overflow-hidden rounded-xl border border-(--color-border) bg-(--color-neutral-2) text-(--color-card-text) shadow-[0_3px_14px_rgba(48,42,54,0.05)]'
            aria-label={recordTitle}
        >
            <div
                className={`flex items-start justify-between gap-2 p-3.5 ${editing ? '' : 'border-b border-(--color-border) bg-(--color-body)'}`}
            >
                <div className='flex min-w-0 items-center gap-2.5'>
                    <span className='flex size-[30px] shrink-0 items-center justify-center rounded-full bg-(--color-primary-1) text-base font-semibold text-(--color-brand-green)'>
                        {rowNumber}
                    </span>
                    <div className='min-w-0'>
                        <p className='text-sm leading-[21px] font-semibold'>
                            {editing ? t('translation:editRow') : recordTitle}
                        </p>
                        <p className='mt-0.5 text-xs leading-[16.5px] text-(--color-card-subtitle)'>
                            {editing
                                ? recordTitle
                                : `${t('translation:fields')}: ${columns.length}`}
                        </p>
                    </div>
                </div>
                {!readOnly && (
                    <div className='flex gap-1.5'>
                        {!editing && onEdit && (
                            <Button
                                {...actionProps}
                                aria-label={t('translation:edit')}
                                disabled={disabled || editDisabled}
                                onClick={onEdit}
                            >
                                <Pencil />
                            </Button>
                        )}
                        <ShadcnButton
                            {...actionProps}
                            aria-label={t('translation:delete')}
                            disabled={disabled}
                            icon={<Trash2 className='text-(--color-danger)' />}
                            popconfirm={{
                                title: t('translation:areYouSure'),
                                onConfirm: onDelete,
                            }}
                        />
                        {editing && (
                            <Button
                                {...actionProps}
                                aria-label={t('translation:cancel')}
                                disabled={disabled}
                                onClick={onCancel}
                            >
                                <X />
                            </Button>
                        )}
                    </div>
                )}
            </div>
            <div className='px-4 pt-3 pb-3.5'>
                {children ?? (
                    <dl className='flex flex-col gap-[13px]'>
                        {columns.map((column) => {
                            const value = formatTableCellDisplayValue(
                                column,
                                row.cells?.[column.id],
                                lang,
                            );
                            return (
                                <div key={column.id} className='min-w-0'>
                                    <dt className='mb-1 text-[11px] leading-[normal] text-(--color-neutral-7)'>
                                        {getColumnTitle(column, lang)}
                                    </dt>
                                    <dd
                                        className={`text-sm leading-[normal] font-medium [overflow-wrap:anywhere] ${value ? '' : 'text-(--color-card-subtitle)'}`}
                                    >
                                        {value || t('translation:enter')}
                                    </dd>
                                </div>
                            );
                        })}
                    </dl>
                )}
            </div>
            {editing && (
                <div className='flex gap-2.5 p-3.5'>
                    <button
                        type='button'
                        className='min-h-[41px] min-w-0 flex-1 rounded-xl bg-(--color-bg-grey-v2) px-3 py-3 text-sm leading-[normal] font-medium text-(--color-muted-text) disabled:opacity-50'
                        disabled={disabled}
                        onClick={onCancel}
                    >
                        {t('translation:cancel')}
                    </button>
                    <button
                        type='button'
                        className='min-h-[41px] min-w-0 flex-1 rounded-xl bg-(--color-brand-green) px-3 py-3 text-sm leading-[normal] font-medium text-white disabled:opacity-50'
                        disabled={disabled}
                        onClick={onSave}
                    >
                        {t('translation:save')}
                    </button>
                </div>
            )}
        </section>
    );
}
